import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmButton, SwipeRow, useCoarse } from "./Swipe.tsx";
import { normalizeBarcode, articleReasons, canBook, defaultLocation, editsMasterData, formatUnits, frozenMonths, frozenUntil, importOfNote, isFreezer, isPantry, lastPlace, openedRest, pantryQtyText, profileFor, profileOf, profileReasons, quantityAt, takeHint } from "@lagerlog/core";
import { IS_PANTRY, MAIN_PROFILE, WORDS, compareLagers, takeWords } from "../brand.ts";
import type { Booking } from "@lagerlog/core";
import { findArticles, pricesShown } from "../catalog.ts";
import type { Article, Catalog } from "../catalog.ts";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import { withdrawalBooked } from "../timing.ts";
import { rememberTake, takeFrom } from "../takeFrom.ts";
import { Scanner } from "./Scanner.tsx";
import { useWide } from "../wide.ts";
import { ReservedHint } from "./Reserved.tsx";
import { Emoji } from "./Emoji.tsx";
import { UnknownCode } from "./UnknownCode.tsx";

type Mode = "out" | "in" | "move";

/** The reasons of the wine profile, for a business or a private cellar; the first is the default. */
const reasonsFor = (mode: Mode, kind: string | undefined, article?: Article | null) =>
  mode === "move" ? [] : article ? articleReasons(article.fields, mode, kind === "private" ? "private" : "business") : profileReasons(MAIN_PROFILE, mode, kind === "private" ? "private" : "business");

/** What a booking is entered in: the bottle and the cartons (factors in ml), else the base unit. */
export function unitsOf(a: Article) {
  return a.units;
}

/** The unit for an opened pack's rest at a place: „1 angebrochen“ takes what is left (A1). */
const OPENED = "angebrochen";

/**
 * The wine's card number first, as the staff orders it ("2 × die 47"). As a tile (the tablet at the
 * bar) the wine and vintage come first and the producer small below, so short tiles still tell.
 */
export function Named({ a, tile = false }: { a: Article; tile?: boolean }) {
  // The pantry has no card numbers: its picture in that column, as in Vorräte and Start (B4).
  if (isPantry(a.fields)) {
    return (
      <span className="named pantry">
        <Emoji fields={a.fields} />
        <span className="name clamp">{a.name}</span>
      </span>
    );
  }
  const no = a.fields.listNo;
  const wine = typeof a.fields.wine === "string" ? a.fields.wine : null;
  const producer = typeof a.fields.producer === "string" ? a.fields.producer : null;
  const vintage = typeof a.fields.vintage === "number" ? ` ${a.fields.vintage}` : "";
  return (
    <span className="named">
      <span className="no">{no ? <span className="listno">{String(no)}</span> : null}</span>
      {tile && wine ? (
        <span className="name">
          <span className="clamp">{`${wine}${vintage}`}</span>
          {producer && <span className="producer muted">{producer}</span>}
        </span>
      ) : (
        <span className="name clamp">{a.name}</span>
      )}
    </span>
  );
}

export function fmt(a: Article | undefined, qty: number) {
  if (!a) return String(qty);
  const pantry = isPantry(a.fields);
  // The pantry shows whole containers and an opened one, never grams of a rest (docs/18-vorratsbuch.md, Einheiten).
  if (pantry) return pantryQtyText(qty, a.fields);
  // A drink in bottles or kegs, never in cartons: loose bottles are no carton (B1); cartons are only entered.
  return formatUnits(qty, a.units.slice(0, 1), a.unit);
}

/** What was entered, and what it is when that was a pack: „3 Kartons à 6 (18 Fl.)“, else as `fmt`. */
export function fmtEntered(a: Article, n: number, factor: number) {
  const pack = !isPantry(a.fields) && factor > a.units[0]!.factor ? a.units.find((u) => u.factor === factor) : undefined;
  return pack ? `${n} ${n === 1 ? pack.name : (pack.plural ?? pack.name)} (${fmt(a, n * factor)})` : fmt(a, n * factor);
}

/** A difference in bottles only, without cartons: „−10 Fl.“ (a rest in ml if any). */
export function fmtDiff(a: Article | undefined, qty: number) {
  if (!a) return String(qty);
  return `${qty > 0 ? "+" : ""}${formatUnits(qty, a.units.slice(0, 1), a.unit)}`;
}

function useFavorites(catalog: Catalog | null, max: number): Article[] {
  const { store, version } = useStore();
  return useMemo(() => {
    if (!catalog || !store.replica) return [];
    const uses = new Map<string, number>();
    const journal = store.replica.journal();
    for (let i = journal.length - 1, n = 0; i >= 0 && n < 2000; i--, n++) {
      const b = journal[i]!;
      if (b.kind === "out") uses.set(b.article, (uses.get(b.article) ?? 0) + 1);
    }
    return [...uses.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, max)
      .map(([id]) => catalog.articles.get(id))
      .filter((a): a is Article => Boolean(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, version, max]);
}

/**
 * Hits shown under the search. It used to be 8: a cellar with 15 wines of one producer then never
 * showed the ninth (a test cellar, 19.10.2026). Now every hit up to this many, and a line for the rest.
 */
const MAX_HITS = 40;

export function Book() {
  const { store } = useStore();
  const catalog = useCatalog();
  const drinks = useDrinks();
  const ledger = useLedger();
  const wide = useWide();
  // The tablet at the bar shows more tiles.
  // On a phone the space under the search is shared: 4 frequent wines, then the last bookings.
  const favorites = useFavorites(catalog, wide ? 12 : 4);
  const [mode, setMode] = useState<Mode>("out");
  const [query, setQuery] = useState("");
  const [article, setArticle] = useState<Article | null>(null);
  const [location, setLocation] = useState("");
  const [target, setTarget] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState(1);
  const [reason, setReason] = useState(() => reasonsFor("out", store.config?.kind)[0]?.id ?? "sale");
  const [price, setPrice] = useState("");
  /** Best-before date of a receipt, for drinks that expire (beer, juice; docs/17-getraenke.md, Fristen). */
  const [bestBefore, setBestBefore] = useState("");
  /** Freezing (C1): the day it is frozen and the new best-before, suggested until changed by hand. */
  const [frozenOn, setFrozenOn] = useState(() => new Date().toLocaleDateString("sv-SE"));
  const [until, setUntil] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [done, setDone] = useState<{ text: string; booking: Booking } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // VorratsBuch: a scanned code no article has (F30), and the article chosen for it, once the catalogue has it.
  const [unknown, setUnknown] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  // Only storages the member may book in (F20); seen-only ones show under Bestand.
  const scope = useMemo(() => new Set((store.config?.lagers ?? []).filter((l) => canBook(store.rights(), l))), [store.config]);
  const locations = useMemo(
    () => {
      if (!catalog) return [];
      // Storages in the order they were set up (B5), locations by name.
      const byLager = compareLagers((id) => catalog.lagers.get(id)?.name ?? id);
      return [...catalog.locations.values()].filter((l) => scope.has(l.lager)).sort((a, b) => byLager(a.lager, b.lager) || a.name.localeCompare(b.name, "de", { numeric: true }));
    },
    [catalog, scope],
  );

  const kind = store.config?.kind;
  // The profile's word for taking out: "Entnehmen", in a private cellar "Trinken".
  const takeWord = MAIN_PROFILE.vocabulary.take[kind === "private" ? "private" : "business"];
  useEffect(() => setReason(mode === "move" ? "" : (reasonsFor(mode, kind, article)[0]?.id ?? "")), [mode, kind, article]);

  /**
   * Amount and unit at a place: one container; taking from the pantry where only an opened pack is
   * left, that rest („1 angebrochen“), never a whole pack that is not there (A1).
   */
  const amountAt = useCallback(
    (a: Article, place: string, factor?: number) => {
      setQty(1);
      const rest = mode !== "in" && ledger && place ? openedRest(quantityAt(ledger, a.id, place), a.fields) : 0;
      setUnit(factor ?? (rest || a.bottle));
    },
    [ledger, mode],
  );

  const choose = useCallback(
    (a: Article, factor?: number, forMode: Mode = mode) => {
      setArticle(a);
      setQuery("");
      setNotice(null);
      // Taking: where this device took it last, or from its last storage (the bar, not the cellar; B3).
      const usual = forMode === "out" && ledger ? takeFrom(a.id, locations, (l) => quantityAt(ledger, a.id, l)) : null;
      // Else where most of it is; without stock where it lay last or where it is kept (A5), a drink in the cellar.
      const place =
        usual ??
        defaultLocation({
          fields: a.fields,
          places: locations.map((l) => ({ id: l.id, lager: l.lager, stock: ledger ? quantityAt(ledger, a.id, l.id) : 0 })),
          lagerProfile: (l) => catalog?.lagers.get(l)?.profile,
          lagerName: (l) => catalog?.lagers.get(l)?.name ?? l,
          last: lastPlace(store.replica?.journal() ?? [], a.id),
        });
      setLocation(place);
      amountAt(a, place, factor);
    },
    [ledger, locations, catalog, store, amountAt, mode],
  );

  // Another mode, another amount in the pantry: receiving never takes „angebrochen“, taking may.
  useEffect(() => {
    if (article && isPantry(article.fields)) amountAt(article, location);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const onCode = useCallback(
    (code: string) => {
      setScanning(false);
      setUnknown(null);
      const hit = catalog?.barcodes.get(code) ?? catalog?.barcodes.get(normalizeBarcode(code) ?? "");
      const a = hit && catalog?.articles.get(hit.article);
      if (a) choose(a, hit.factor);
      // VorratsBuch asks Open Food Facts and offers to assign or create (F30); KellerLog says it is unknown.
      else if (IS_PANTRY) setUnknown(code);
      else setNotice(`Unbekannter Strichcode ${code}`);
    },
    [catalog, choose],
  );

  // A new or assigned article comes through the catalogue a moment later: then it is chosen.
  useEffect(() => {
    const a = pending ? catalog?.articles.get(pending) : undefined;
    if (!a) return;
    setPending(null);
    setUnknown(null);
    choose(a);
  }, [pending, catalog, choose]);

  const matches = useMemo(() => {
    if (!catalog || query.length < 2) return [];
    return findArticles(catalog.articles.values(), query);
  }, [catalog, query]);

  // From the list of wines: "#/buchen?einlagern=<id>" opens the booking for that wine.
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
    const wanted = params.get("einlagern") ?? params.get("entnehmen") ?? params.get("einfrieren");
    // "#/buchen?einlagern" without an article: just the mode (start page of VorratsBuch).
    if (params.has("einlagern") && !wanted) {
      setMode("in");
      history.replaceState(null, "", "#/buchen");
      return;
    }
    const a = wanted ? catalog?.articles.get(wanted) : undefined;
    if (!a) return;
    if (params.has("einfrieren")) {
      // From the pantry (C1): from where most of it lies outside a freezer into the first freezer.
      setMode("move");
      choose(a, undefined, "move");
      const freezer = (l: { lager: string }) => isFreezer({ id: l.lager, name: catalog?.lagers.get(l.lager)?.name ?? "" });
      const from = locations.filter((l) => !freezer(l)).sort((x, y) => quantityAt(ledger!, a.id, y.id) - quantityAt(ledger!, a.id, x.id))[0];
      if (from) {
        setLocation(from.id);
        amountAt(a, from.id);
      }
      setTarget(locations.find(freezer)?.id ?? "");
      history.replaceState(null, "", "#/buchen");
      return;
    }
    const wantedMode = params.has("einlagern") ? "in" : "out";
    setMode(wantedMode);
    choose(a, undefined, wantedMode);
    history.replaceState(null, "", "#/buchen");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog !== null]);

  if (!catalog || !ledger) return null;
  if (scope.size === 0) {
    return (
      <section className="card" data-testid="nothing-to-book">
        <h2>Hier darfst du nichts buchen</h2>
        <p>
          Du siehst den Bestand deiner Lager unter <a href="#/bestand">Bestand</a>. Wo du buchen darfst, legt ein Inhaber unter Konto → Betrieb fest.
        </p>
      </section>
    );
  }
  const loc = catalog.locations.get(location);
  const amount = qty * unit;
  // Into a freezer from elsewhere, food with lots: frozen, with a new best-before (C1).
  const lagerOfPlace = (id: string) => {
    const lager = catalog.locations.get(id)?.lager ?? "";
    return { id: lager, name: catalog.lagers.get(lager)?.name ?? "" };
  };
  const freezing = mode === "move" && !!article && isPantry(article.fields) && article.tracking === "lot" && !!target && isFreezer(lagerOfPlace(target)) && !isFreezer(lagerOfPlace(location));
  const frozenBestBefore = until ?? (article ? frozenUntil(article.fields, frozenOn) : "");
  const here = article && location ? quantityAt(ledger, article.id, location) : 0;
  const rest = article && mode !== "in" ? openedRest(here, article.fields) : 0;

  async function submit() {
    if (!article || !loc || amount <= 0) return;
    let booking: Booking;
    if (mode === "move") {
      const to = catalog!.locations.get(target);
      if (!to || to.id === loc.id) return;
      booking = await store.book({
        kind: "move",
        article: article.id,
        fromLager: loc.lager,
        from: loc.id,
        toLager: to.lager,
        to: to.id,
        qty: amount,
        ...(freezing && frozenBestBefore ? { lotInfo: { bestBefore: frozenBestBefore, frozen: frozenOn } } : {}),
        ...(rest > 0 && unit === rest ? { entered: { qty, unit: OPENED } } : {}),
      } as Booking);
    } else {
      const cents = Math.round(Number(price.replace(",", ".")) * 100);
      booking = await store.book({
        kind: mode,
        article: article.id,
        lager: loc.lager,
        location: loc.id,
        qty: amount,
        reason,
        // The price is per unit entered (a bottle, a carton); the receipt keeps the amount.
        ...(mode === "in" && cents > 0 ? { amount: cents * qty } : {}),
        ...(mode === "in" && bestBefore ? { lotInfo: { bestBefore } } : {}),
        ...(unit !== article.bottle ? { entered: { qty, unit: unitsOf(article).find((u) => u.factor === unit)?.name ?? (unit === rest ? OPENED : "") } } : {}),
      } as Booking);
      if (mode === "out") {
        withdrawalBooked();
        rememberTake(article.id, loc.id, loc.lager);
      }
    }
    const verb = mode === "out" ? takeWords(kind).taken : mode === "in" ? "Eingelagert" : freezing ? "Eingefroren" : "Umgelagert";
    setDone({ text: `${verb}: ${fmtEntered(article, qty, unit)} ${article.name}`, booking });
    setArticle(null);
    setPrice("");
    setBestBefore("");
    setUntil(null);
    setTimeout(() => setDone((d) => (d?.booking === booking ? null : d)), 6000);
  }

  async function cancel(b: Booking) {
    const lagers = b.kind === "move" ? [...new Set([b.fromLager, b.toLager])] : b.kind === "cancel" ? b.lagers : [b.lager];
    await store.book({ kind: "cancel", cancels: b.id, lagers } as Booking);
  }

  async function undo() {
    if (!done) return;
    await cancel(done.booking);
    setDone(null);
  }

  const segmented = (
    <div className="segmented">
      {(["out", "in", "move"] as const).map((m) => (
        <button key={m} className={mode === m ? "active" : ""} onClick={() => setMode(m)}>
          {m === "out" ? takeWord : m === "in" ? "Einlagern" : "Umlagern"}
        </button>
      ))}
    </div>
  );

  const finder = (
    <>
      <div className="row">
        <input placeholder={drinks ? "Getränk suchen" : MAIN_PROFILE.vocabulary.search} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="search" />
        <button className="primary" onClick={() => setScanning(true)}>
          Scannen
        </button>
      </div>
      {matches.length > 0 && (
        <ul className="list" data-testid="search-hits">
          {matches.slice(0, MAX_HITS).map((a) => (
            <li key={a.id}>
              <button onClick={() => choose(a)}>
                <Named a={a} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {IS_PANTRY && query.trim().length >= 2 && matches.length === 0 && (
        // Nothing found in the pantry: say so, and let owners and managers add it (A4).
        <div className="nothing-found" data-testid="search-none">
          <p className="muted">Nichts gefunden.</p>
          {editsMasterData(store.rights().role) && (
            <a className="button wide" href={`#/vorraete?neu=${encodeURIComponent(query.trim())}`} data-testid="search-new">
              „{query.trim()}“ neu anlegen
            </a>
          )}
        </div>
      )}
      {matches.length > MAX_HITS && <p className="hint">… und {matches.length - MAX_HITS} weitere. Ein Wort mehr grenzt ein, etwa der Jahrgang.</p>}
      {query.length < 2 && favorites.length > 0 && (
        <>
          <h3>Häufig</h3>
          <div className={wide ? "tiles" : "tiles two"}>
            {favorites.map((a) => (
              <button key={a.id} className={article?.id === a.id ? "active" : ""} onClick={() => choose(a)}>
                <Named a={a} tile />
              </button>
            ))}
          </div>
        </>
      )}
      {notice && <p className="error">{notice}</p>}
      {unknown && catalog && <UnknownCode key={unknown} code={unknown} catalog={catalog} onChosen={setPending} onClose={() => setUnknown(null)} />}
      {!wide && query.length < 2 && <Recent catalog={catalog} cancelled={ledger.cancelled} onCancel={cancel} short />}
    </>
  );

  const form = article && (
    <div className="card">
      <div className="row between">
        <strong>{article.name}</strong>
        <button className="link" onClick={() => setArticle(null)}>
          {wide ? "abbrechen" : "ändern"}
        </button>
      </div>
      {mode === "out" && <ReservedHint a={article} catalog={catalog} />}
      <label>
        {mode === "move" ? "Von" : "Lagerort"}
        <select
          value={location}
          onChange={(e) => {
            setLocation(e.target.value);
            amountAt(article, e.target.value);
          }}
          data-testid="location"
        >
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {catalog.lagers.get(l.lager)?.name} · {l.name} ({fmt(article, quantityAt(ledger, article.id, l.id))})
            </option>
          ))}
        </select>
      </label>
      {mode === "move" && (
        <label>
          Nach
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">bitte wählen</option>
            {locations
              .filter((l) => l.id !== location)
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {catalog.lagers.get(l.lager)?.name} · {l.name}
                </option>
              ))}
          </select>
        </label>
      )}
      <div className="row">
        <div className="stepper">
          <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="weniger">
            −
          </button>
          <input inputMode="numeric" value={qty} onChange={(e) => setQty(Math.max(0, Math.floor(Number(e.target.value) || 0)))} data-testid="qty" />
          <button onClick={() => setQty(qty + 1)} aria-label="mehr">
            +
          </button>
        </div>
        <select value={unit} onChange={(e) => setUnit(Number(e.target.value))} data-testid="unit">
          {unitsOf(article).map((u) => (
            <option key={u.name} value={u.factor}>
              {u.name}
            </option>
          ))}
          {rest > 0 && <option value={rest}>{OPENED}</option>}
        </select>
      </div>
      {freezing && (
        <div className="freeze" data-testid="freeze">
          <label>
            Eingefroren am
            <input type="date" value={frozenOn} onChange={(e) => setFrozenOn(e.target.value)} data-testid="frozen-on" />
          </label>
          <label>
            Haltbar bis
            <input type="date" value={frozenBestBefore} onChange={(e) => setUntil(e.target.value)} data-testid="frozen-until" />
          </label>
          <p className="hint">Vorgeschlagen: {frozenMonths(article.fields)} Monate ab dem Einfrieren. Das MHD von vorher gilt danach nicht mehr.</p>
        </div>
      )}
      {mode !== "move" && (
        <label>
          Grund
          <select value={reason} onChange={(e) => setReason(e.target.value)}>
            {reasonsFor(mode, kind, article).map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {mode === "in" && pricesShown(catalog, store.prices) && (
        <label>
          Preis je {unitsOf(article).find((u) => u.factor === unit)?.name} (€)
          <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
      )}
      {mode === "in" && article.tracking === "lot" && profileFor(article.fields).expires && (
        <label>
          MHD (wenn auf dem Etikett)
          <input type="date" value={bestBefore} onChange={(e) => setBestBefore(e.target.value)} data-testid="best-before" />
        </label>
      )}
      {mode === "out" && loc && (() => {
        const hint = takeHint(ledger, article.fields, article.id, loc.id, Date.now());
        return hint ? (
          <p className="warn" data-testid="take-hint">
            {hint}
          </p>
        ) : null;
      })()}
      {mode !== "in" && here - amount < 0 && <p className="warn">Bestand hier: {fmt(article, here)}. Die Buchung wird trotzdem angenommen und markiert.</p>}
      <button className="primary big" onClick={submit} data-testid="submit">
        {mode === "out" ? takeWord : mode === "in" ? "Einlagern" : freezing ? "Einfrieren" : "Umlagern"}
      </button>
    </div>
  );

  const overlays = (
    <>
      {done && (
        <div className="toast" data-testid="toast">
          {done.text}
          <button onClick={undo}>Rückgängig</button>
        </div>
      )}
      {scanning && <Scanner onCode={onCode} onClose={() => setScanning(false)} />}
    </>
  );

  if (wide) {
    return (
      <div className="split book wide-left">
        <div className="pane">
          {segmented}
          {finder}
        </div>
        <div className="pane side-pane">
          {form || (
            <div className="card placeholder">
              <p>Links {drinks ? "ein Getränk" : WORDS.aOne} antippen, suchen oder scannen. Menge, Lagerort und Grund sind vorbelegt.</p>
            </div>
          )}
          <Recent catalog={catalog} cancelled={ledger.cancelled} onCancel={cancel} />
        </div>
        {overlays}
      </div>
    );
  }

  return (
    <div className="book">
      {segmented}
      {!article ? finder : form}
      {overlays}
    </div>
  );
}

const clock = (at: number) => new Date(at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/** The last bookings of this person today, each with "Rückgängig" (P2: an undo, not a question). */
function Recent({ catalog, cancelled, onCancel, short }: { catalog: Catalog; cancelled: Set<string>; onCancel: (b: Booking) => Promise<void>; short?: boolean }) {
  const { store } = useStore();
  const [all, setAll] = useState(false);
  const coarse = useCoarse();
  const today = new Date().setHours(0, 0, 0, 0);
  const mine = (b: Booking) => (store.config?.userId ? b.person === store.config.userId : b.device === store.config?.device);
  const journal = store.replica?.journal() ?? [];
  // The opening stock of an import is one line, without undo: the import's record has it all (B6).
  type Opening = { kind: "import"; id: string; at: number; articles: Set<string> };
  const recent: (Booking | Opening)[] = [];
  const openings = new Map<string, Opening>();
  for (let i = journal.length - 1; i >= 0 && recent.length < 8; i--) {
    const b = journal[i]!;
    if (b.at < today) break;
    if (!((b.kind === "out" || b.kind === "in" || b.kind === "move") && mine(b))) continue;
    const from = b.kind === "in" ? importOfNote(b.note) : null;
    if (from === null) recent.push(b);
    else if (!cancelled.has(b.id)) {
      const o = openings.get(from);
      if (o) o.articles.add(b.article);
      else {
        const created: Opening = { kind: "import", id: from, at: b.at, articles: new Set([b.article]) };
        openings.set(from, created);
        recent.push(created);
      }
    }
  }
  if (recent.length === 0) return null;
  const opening = (o: Opening) => {
    const file = catalog.imports.get(o.id)?.file;
    const wines = [...o.articles].every((id) => profileOf(catalog.articles.get(id)?.fields ?? {}) === "wein");
    const n = o.articles.size;
    const what = wines ? (n === 1 ? "Wein" : "Weine") : n === 1 ? "Getränk" : "Getränke";
    return `${file ? `aus ${file}: ` : ""}${n} ${what}`;
  };
  return (
    <section className="recent">
      <h3 className="row between">
        <span>Zuletzt gebucht</span>
        {coarse && <span className="muted small">nach rechts wischen = rückgängig</span>}
      </h3>
      <ul className="list">
        {(short && !all ? recent.slice(0, 3) : recent).map((b) => {
          if (b.kind === "import")
            return (
              <li key={`import-${b.id}`} className="entry" data-testid="recent-import">
                <span className="entry-text">
                  <span className="muted">{clock(b.at)} · Startbestand</span>
                  <br />
                  {opening(b)}
                </span>
              </li>
            );
          if (b.kind !== "out" && b.kind !== "in" && b.kind !== "move") return null;
          const a = catalog.articles.get(b.article);
          const gone = cancelled.has(b.id);
          return (
            <SwipeRow key={b.id} label="Rückgängig" off={gone || !coarse} onSwipe={() => void onCancel(b)} className={`entry${gone ? " gone" : ""}`} testid="recent-entry">
              <span className="entry-text">
                <span className="muted">
                  {clock(b.at)} · {b.kind === "out" ? takeWords(store.config?.kind).taken : b.kind === "in" ? "Eingelagert" : b.lotInfo?.frozen ? "Eingefroren" : "Umgelagert"}
                  {gone ? " · storniert" : ""}
                </span>
                <br />
                {fmt(a, b.qty)} {a?.name ?? b.article}
              </span>
              {!coarse && !gone && <ConfirmButton label="Rückgängig" onConfirm={() => onCancel(b)} />}
            </SwipeRow>
          );
        })}
      </ul>
      {short && !all && recent.length > 3 && (
        <button className="link" onClick={() => setAll(true)}>
          {recent.length - 3} weitere
        </button>
      )}
    </section>
  );
}
